/**
 * LeaderReviewSelf.tsx
 * Self-assessment: card-by-card, one muscle at a time.
 *
 * Wizard flow:
 *   Step 1–N  — one muscle per card (score + evidence + impact tags)
 *   Step N+1  — OKR Reflection: per-KR anchor reflection before final submit
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchSession, fetchSelfAssessment, saveSelfAssessmentDraft, submitSelfAssessment,
  computeKRInsight, resolveSessionMuscles,
} from '../services/leaderReviewService';
import { fetchKRsForReview } from '../services/evidenceService';
import { LEADER_MUSCLES } from '../types';
import type {
  LeaderReviewSession, LeaderMuscleScore, KRProgressEntry,
  LeaderMuscle, KRReflection,
} from '../types';

// ─── Constants ────────────────────────────────────────────────────────────────

const IMPACT_TAGS = ['Drives momentum', 'Removes blockers', 'Aligns team', 'Delivers outcomes'];

const SCORE_LABELS: Record<number, string> = {
  1: 'Weak',
  2: 'Inconsistent',
  3: 'Solid',
  4: 'Strong',
  5: 'Leading',
};

const SCORE_COLORS: Record<number, { ring: string; bg: string; text: string; bgLight: string; border: string }> = {
  1: { ring: 'ring-red-400',    bg: 'bg-red-500',    text: 'text-red-600',    bgLight: 'bg-red-50',    border: 'border-red-200'    },
  2: { ring: 'ring-orange-400', bg: 'bg-orange-500', text: 'text-orange-600', bgLight: 'bg-orange-50', border: 'border-orange-200' },
  3: { ring: 'ring-amber-400',  bg: 'bg-amber-500',  text: 'text-amber-600',  bgLight: 'bg-amber-50',  border: 'border-amber-200'  },
  4: { ring: 'ring-blue-400',   bg: 'bg-blue-500',   text: 'text-blue-600',   bgLight: 'bg-blue-50',   border: 'border-blue-200'   },
  5: { ring: 'ring-emerald-400',bg: 'bg-emerald-500',text: 'text-emerald-600',bgLight: 'bg-emerald-50',border: 'border-emerald-200'},
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">
      {children}
    </p>
  );
}

function isKRDone(kr: KRProgressEntry) {
  return (kr.progress ?? 0) >= 100 || (kr.krStatus ?? '').toLowerCase().includes('achiev');
}

// ─── Score selector ───────────────────────────────────────────────────────────

function DotScore({ value, onChange, rubric }: {
  value: number; onChange: (v: number) => void; rubric: Record<number, string>;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 justify-center">
        {[1, 2, 3, 4, 5].map(n => {
          const active = value === n;
          const col = SCORE_COLORS[n];
          return (
            <button
              key={n}
              onClick={() => onChange(n)}
              className="flex flex-col items-center gap-2 group transition-all"
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-base font-bold transition-all
                ${active
                  ? `${col.bg} text-white scale-110 shadow-md ring-4 ${col.ring} ring-offset-2`
                  : 'bg-white border-2 border-gray-300 text-gray-600 hover:border-gray-500 hover:text-gray-800 hover:scale-105'
                }`}
              >
                {n}
              </div>
              <span className={`text-xs font-semibold leading-tight text-center w-14 ${active ? col.text : 'text-gray-500'}`}>
                {SCORE_LABELS[n]}
              </span>
            </button>
          );
        })}
      </div>
      {value > 0 && rubric[value] && (
        <div className={`rounded-xl px-4 py-3 border ${SCORE_COLORS[value].bgLight} ${SCORE_COLORS[value].border}`}>
          <p className={`text-sm leading-relaxed font-medium italic ${SCORE_COLORS[value].text}`}>
            "{rubric[value]}"
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

type WizardStep = 'muscles' | 'okr_reflection';

const LeaderReviewSelf: React.FC = () => {
  const { sessionId }  = useParams<{ sessionId: string }>();
  const { user }       = useAuth();
  const navigate       = useNavigate();

  const [session, setSession]       = useState<LeaderReviewSession | null>(null);
  const [muscles, setMuscles]       = useState<LeaderMuscle[]>(LEADER_MUSCLES);
  const [scores, setScores]         = useState<LeaderMuscleScore[]>(
    LEADER_MUSCLES.map(m => ({ muscleKey: m.key, score: 0, example: '', impactTags: [] })),
  );
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showAnchor, setShowAnchor] = useState(false);
  const [wizardStep, setWizardStep] = useState<WizardStep>('muscles');

  // OKR data
  const [krs, setKRs]                       = useState<KRProgressEntry[]>([]);
  const [loadingKRs, setLoadingKRs]         = useState(true);
  const [krReflections, setKrReflections]   = useState<KRReflection[]>([]);

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = useCallback(async () => {
    if (!sessionId || !user?.uid) return;
    setLoading(true);
    try {
      const [sess, existing, myKRs] = await Promise.all([
        fetchSession(sessionId),
        fetchSelfAssessment(sessionId, user.uid),
        fetchKRsForReview(user.uid).catch(() => [] as KRProgressEntry[]),
      ]);
      setSession(sess);
      setKRs(myKRs);
      setLoadingKRs(false);
      const resolvedMuscles = sess ? await resolveSessionMuscles(sess) : LEADER_MUSCLES;
      setMuscles(resolvedMuscles);
      if (existing?.scores?.length) {
        setScores(existing.scores);
        if (existing.submittedAt) setSubmitted(true);
      } else {
        setScores(resolvedMuscles.map(m => ({ muscleKey: m.key, score: 0, example: '', impactTags: [] })));
      }
      // Load saved KR reflections if any
      if (existing?.krReflections?.length) {
        setKrReflections(existing.krReflections);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Init krReflections from fetched KRs (only if not already loaded from Firestore)
  useEffect(() => {
    if (krs.length > 0 && krReflections.length === 0) {
      setKrReflections(krs.map(kr => ({
        krId: kr.krId,
        krTitle: kr.krTitle,
        krNumber: kr.krNumber,
        status: kr.krStatus ?? 'In Progress',
        progress: kr.progress ?? 0,
        reflection: '',
      })));
    }
  }, [krs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset anchor when changing muscle
  useEffect(() => { setShowAnchor(false); }, [currentIdx]);

  // ── Derived ──────────────────────────────────────────────────────────────────

  const currentMuscle = muscles[currentIdx] ?? muscles[0];
  const currentScore  = currentMuscle
    ? scores.find(s => s.muscleKey === currentMuscle.key) ?? { muscleKey: currentMuscle.key, score: 0, example: '', impactTags: [] }
    : { muscleKey: '', score: 0, example: '', impactTags: [] };

  const scored      = scores.filter(s => s.score > 0).length;
  const progressPct = muscles.length ? Math.round((scored / muscles.length) * 100) : 0;
  const allScored   = muscles.length > 0 && scored === muscles.length;

  const krAchieved    = krs.filter(isKRDone).length;
  const krPct         = krs.length ? Math.round((krAchieved / krs.length) * 100) : 0;
  const krInsight     = computeKRInsight(krs);

  // ── Update helpers ────────────────────────────────────────────────────────────

  const updateCurrentScore = (patch: Partial<LeaderMuscleScore>) => {
    setScores(prev => prev.map(s =>
      s.muscleKey === currentMuscle.key ? { ...s, ...patch } : s
    ));
  };

  const setScore        = (val: number) => updateCurrentScore({ score: val });
  const setExample      = (val: string) => updateCurrentScore({ example: val });
  const toggleImpactTag = (tag: string) => {
    const existing = currentScore.impactTags ?? [];
    updateCurrentScore({
      impactTags: existing.includes(tag)
        ? existing.filter(t => t !== tag)
        : [...existing, tag],
    });
  };

  const updateKRReflection = (krId: string, reflection: string) => {
    setKrReflections(prev => prev.map(r => r.krId === krId ? { ...r, reflection } : r));
  };

  // ── Auto-save & navigation ────────────────────────────────────────────────────

  const autoSave = useCallback(async () => {
    if (!sessionId || !user?.uid || saving) return;
    setSaving(true);
    try { await saveSelfAssessmentDraft(sessionId, user.uid, scores, krReflections); }
    finally { setSaving(false); }
  }, [sessionId, user?.uid, scores, krReflections, saving]);

  const goNext = async () => {
    await autoSave();
    if (currentIdx < muscles.length - 1) {
      setCurrentIdx(i => i + 1);
    } else {
      // Last muscle → go to OKR reflection step
      setWizardStep('okr_reflection');
    }
  };

  const goBack = async () => {
    await autoSave();
    if (currentIdx > 0) setCurrentIdx(i => i - 1);
  };

  const handleSubmit = async () => {
    if (!sessionId || !user?.uid) return;
    if (!allScored) {
      alert(`Please score all ${muscles.length} muscles before submitting.`);
      return;
    }
    setSaving(true);
    try {
      await submitSelfAssessment(sessionId, user.uid, scores, krReflections);
      // Re-fetch session — may have auto-advanced to peer_review
      const updated = await fetchSession(sessionId);
      if (updated) setSession(updated);
      setSubmitted(true);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-5 h-5 border-2 border-[#011132] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ── Submitted state ───────────────────────────────────────────────────────────

  if (submitted) {
    const firstName = (user?.name || '').split(' ')[0] || 'there';
    const STATUS_ORDER = ['setup', 'self_review', 'peer_review', 'live', 'consolidation', 'completed'];
    const sessionStatusIdx = STATUS_ORDER.indexOf(session?.status ?? 'self_review');

    const stageStateOf = (stageStatus: string): 'done' | 'active' | 'next' | 'upcoming' => {
      if (stageStatus === 'self_review') return 'done';
      const idx = STATUS_ORDER.indexOf(stageStatus);
      if (idx < sessionStatusIdx) return 'done';
      if (idx === sessionStatusIdx) return 'active';
      if (idx === sessionStatusIdx + 1) return 'next';
      return 'upcoming';
    };

    const stages = [
      { key: 'self_review', label: 'Self Review',   icon: 'edit_note',  date: session?.selfReviewDeadline ?? null },
      { key: 'peer_review', label: 'Peer Review',   icon: 'group',      date: session?.peerReviewDeadline ?? null },
      { key: 'live',        label: 'Live Session',  icon: 'video_call', date: session?.liveSessionDate ?? null },
      { key: 'completed',   label: 'Results',       icon: 'insights',   date: null },
    ];

    const peerReviewState = stageStateOf('peer_review');
    const peerOpen = peerReviewState === 'active';

    return (
      <div className="max-w-lg mx-auto px-6 py-10">

        {/* Success header */}
        <div className="text-center mb-7">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="material-symbols-rounded text-emerald-600" style={{ fontSize: 32 }}>check_circle</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Self-Assessment Submitted!</h2>
          <p className="text-sm text-gray-600">
            Nice work, {firstName} — your responses are locked and ready for review.
          </p>
        </div>

        {/* Stage journey */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-4">
          <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">Your Journey</p>
          <div className="space-y-0">
            {stages.map((stage, i) => {
              const state = stageStateOf(stage.key);
              const isLast = i === stages.length - 1;
              return (
                <div key={stage.key} className="flex items-stretch gap-3">
                  {/* Left column: dot + line */}
                  <div className="flex flex-col items-center flex-shrink-0 w-7">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center z-10 flex-shrink-0
                      ${state === 'done'    ? 'bg-emerald-500' :
                        state === 'active'  ? 'bg-blue-500' :
                        state === 'next'    ? 'bg-amber-100 border-2 border-amber-400' :
                        'bg-gray-100 border border-gray-200'}`}
                    >
                      {state === 'done' ? (
                        <span className="material-symbols-rounded text-white" style={{ fontSize: 14 }}>check</span>
                      ) : (
                        <span className={`material-symbols-rounded ${
                          state === 'active' ? 'text-white' : state === 'next' ? 'text-amber-600' : 'text-gray-400'
                        }`} style={{ fontSize: 14 }}>{stage.icon}</span>
                      )}
                    </div>
                    {!isLast && (
                      <div className={`w-0.5 flex-1 my-1 min-h-[20px] ${state === 'done' ? 'bg-emerald-200' : 'bg-gray-200'}`} />
                    )}
                  </div>
                  {/* Right column: text */}
                  <div className={`flex-1 min-w-0 ${!isLast ? 'pb-4' : ''}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-sm font-semibold ${
                        state === 'done'    ? 'text-emerald-700' :
                        state === 'active'  ? 'text-blue-700' :
                        state === 'next'    ? 'text-amber-700' :
                        'text-gray-400'}`}
                      >
                        {stage.label}
                      </p>
                      {state === 'done'   && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">Done</span>}
                      {state === 'active' && <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />Open Now</span>}
                      {state === 'next'   && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Next</span>}
                    </div>
                    {stage.date && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {state === 'done' ? 'Deadline was' : 'Deadline'}: {stage.date}
                      </p>
                    )}
                    {!stage.date && state === 'upcoming' && stage.key === 'completed' && (
                      <p className="text-xs text-gray-400 mt-0.5">After live session</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Next action card */}
        {peerOpen ? (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-rounded text-blue-600" style={{ fontSize: 18 }}>group</span>
              <p className="text-sm font-bold text-blue-800">Peer Review is now open!</p>
            </div>
            <p className="text-xs text-blue-700 mb-3 leading-relaxed">
              Rate your peers on leadership muscles.{session?.peerReviewDeadline ? ` Due by ${session.peerReviewDeadline}.` : ''}
            </p>
            <button
              onClick={() => navigate(`/leader-review/peers/${sessionId}`)}
              className="w-full bg-blue-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16 }}>arrow_forward</span>
              Start Peer Review Now
            </button>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
              <p className="text-sm font-bold text-amber-800">Peer Review coming up next</p>
            </div>
            <p className="text-xs text-amber-700 leading-relaxed">
              {session?.peerReviewDeadline
                ? `You'll be asked to rate your peers once the admin opens peer review. Deadline: ${session.peerReviewDeadline}.`
                : 'Peer review will open once the admin advances the session. Check back soon.'
              }
            </p>
          </div>
        )}

        <button
          onClick={() => navigate('/leader-review')}
          className="w-full bg-[#011132] text-white text-sm font-medium py-3 rounded-xl hover:bg-[#0a2254] transition-colors"
        >
          ← Back to Leader EPR
        </button>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // OKR REFLECTION STEP
  // ════════════════════════════════════════════════════════════════════════════

  if (wizardStep === 'okr_reflection') return (
    <div className="max-w-2xl mx-auto px-6 py-8">

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => setWizardStep('muscles')}
          className="text-gray-500 hover:text-gray-800 transition-colors"
        >
          <span className="material-symbols-rounded" style={{ fontSize: 22 }}>arrow_back</span>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900">OKR Reflection</h1>
          <p className="text-sm text-gray-600">{session?.name}</p>
        </div>
        <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full flex-shrink-0">
          Final Step
        </span>
      </div>

      {/* OKR Delivery Summary banner */}
      {loadingKRs ? (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-5 animate-pulse">
          <div className="h-4 bg-amber-100 rounded w-1/3 mb-2" />
          <div className="h-2 bg-amber-100 rounded-full w-full" />
        </div>
      ) : krs.length > 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="material-symbols-rounded text-amber-600 flex-shrink-0" style={{ fontSize: 18 }}>flag</span>
              <p className="text-sm font-bold text-amber-900">My OKR Delivery</p>
            </div>
            <span className="text-sm font-bold text-amber-800">{krAchieved}/{krs.length} KRs · {krPct}%</span>
          </div>
          <div className="h-2 bg-amber-100 rounded-full overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all ${krPct >= 80 ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${krPct}%` }}
            />
          </div>
          <p className="text-xs text-amber-800 leading-relaxed">{krInsight}</p>
        </div>
      ) : null}

      {/* Intro text */}
      <p className="text-sm font-medium text-gray-700 mb-4 leading-relaxed">
        Reflect briefly on your KR delivery. This gives your manager and facilitator context behind the numbers — what drove results, what held things back.
      </p>

      {/* KR reflection list */}
      {krs.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center text-gray-500 text-sm mb-5">
          No KRs found for this period. You can proceed to submit.
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {krs.map((kr, i) => {
            const done = isKRDone(kr);
            const pct  = Math.min(kr.progress ?? 0, 100);
            const ref  = krReflections.find(r => r.krId === kr.krId);
            return (
              <div
                key={kr.krId}
                className={`bg-white border rounded-2xl overflow-hidden ${done ? 'border-emerald-200' : 'border-gray-200'}`}
              >
                {/* KR header row */}
                <div className="px-4 pt-4 pb-3">
                  <div className="flex items-start gap-2 mb-2">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5 ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                      {kr.krNumber || `KR ${i + 1}`}
                    </span>
                    <p className="text-sm font-medium text-gray-800 leading-snug flex-1">{kr.krTitle}</p>
                  </div>
                  {/* Progress bar + status */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${done ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-400' : 'bg-amber-400'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className={`text-xs font-bold ${done ? 'text-emerald-700' : 'text-blue-700'}`}>{pct}%</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                      {done ? 'Achieved' : kr.krStatus || 'In Progress'}
                    </span>
                  </div>
                </div>

                {/* Reflection textarea */}
                <div className="px-4 pb-4">
                  <label className={`text-xs font-bold uppercase tracking-wider block mb-1.5 ${done ? 'text-emerald-700' : 'text-gray-600'}`}>
                    {done
                      ? 'What drove this success?'
                      : 'What happened? What would you do differently?'
                    }
                    {' '}<span className="normal-case font-normal text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    rows={2}
                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#011132]/40 focus:ring-2 focus:ring-[#011132]/10 resize-none transition-colors"
                    placeholder={done
                      ? 'What made this possible — team, decisions, momentum…'
                      : "Context, blockers, lessons learned, or what you'd change\u2026"
                    }
                    value={ref?.reflection ?? ''}
                    onChange={e => updateKRReflection(kr.krId, e.target.value)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={!allScored || saving}
        className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white text-sm font-semibold py-3.5 rounded-xl disabled:opacity-40 hover:bg-emerald-700 transition-colors"
      >
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Submitting…
          </>
        ) : (
          <>
            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>check_circle</span>
            Submit Self-Assessment
          </>
        )}
      </button>
      {!allScored && (
        <p className="text-xs text-amber-700 text-center mt-2 font-medium">
          You still have {muscles.length - scored} muscle{muscles.length - scored !== 1 ? 's' : ''} unscored — go back to complete them.
        </p>
      )}
      {allScored && (
        <p className="text-xs text-gray-500 text-center mt-2">
          Your self-assessment will be locked after submission.
        </p>
      )}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // MUSCLE WIZARD STEP
  // ════════════════════════════════════════════════════════════════════════════

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate('/leader-review')} className="text-gray-500 hover:text-gray-800 transition-colors">
          <span className="material-symbols-rounded" style={{ fontSize: 22 }}>arrow_back</span>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900">Self-Assessment</h1>
          <p className="text-sm text-gray-600">{session?.name}</p>
        </div>
        {saving && <span className="text-xs text-gray-500 animate-pulse">Saving…</span>}
      </div>

      {/* KR mini card */}
      {loadingKRs ? (
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5 animate-pulse">
          <div className="h-3 bg-amber-100 rounded w-1/2" />
        </div>
      ) : krs.length > 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
          <div className="flex items-start gap-3">
            <span className="material-symbols-rounded text-amber-600 flex-shrink-0 mt-0.5" style={{ fontSize: 18 }}>flag</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900">
                {krAchieved} / {krs.length} KRs Achieved — {krPct}%
              </p>
              <p className="text-sm text-amber-800 mt-0.5 leading-relaxed">{krInsight}</p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-[#011132] rounded-full transition-all duration-300" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="text-sm font-semibold text-gray-700 flex-shrink-0">{scored} / {muscles.length} scored</span>
      </div>

      {/* Muscle dots nav */}
      <div className="flex items-center gap-2 justify-center mb-6 flex-wrap">
        {muscles.map((m, i) => {
          const s       = scores.find(sc => sc.muscleKey === m.key);
          const done    = (s?.score ?? 0) > 0;
          const current = i === currentIdx;
          return (
            <button
              key={m.key}
              onClick={() => setCurrentIdx(i)}
              title={m.name}
              className={`w-8 h-8 rounded-full text-xs font-bold transition-all
                ${current
                  ? 'bg-[#011132] text-white scale-110 shadow-sm'
                  : done
                  ? 'bg-[#011132]/20 text-[#011132] hover:bg-[#011132]/30'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
            >
              {i + 1}
            </button>
          );
        })}
        {/* OKR Reflection dot */}
        <button
          onClick={() => setWizardStep('okr_reflection')}
          title="OKR Reflection"
          className="w-8 h-8 rounded-full text-xs font-bold transition-all bg-amber-100 text-amber-700 hover:bg-amber-200 flex items-center justify-center"
        >
          <span className="material-symbols-rounded" style={{ fontSize: 14 }}>flag</span>
        </button>
      </div>

      {/* ── Muscle card ── */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">

        {/* Card header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Muscle {currentIdx + 1} of {muscles.length}
              </span>
              <h2 className="text-xl font-bold text-gray-900 mt-1 leading-tight">{currentMuscle.name}</h2>
              <p className="text-sm text-gray-600 mt-1">{currentMuscle.shortDesc}</p>
            </div>
            {currentMuscle.goodLooksLike && (
              <button
                onClick={() => setShowAnchor(prev => !prev)}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors flex-shrink-0 mt-1 ${
                  showAnchor
                    ? 'bg-[#011132] text-white border-[#011132]'
                    : 'text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100'
                }`}
              >
                <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
                  {showAnchor ? 'close' : 'info'}
                </span>
                {showAnchor ? 'Close' : 'Good vs Weak'}
              </button>
            )}
          </div>

          {showAnchor && currentMuscle.goodLooksLike && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">Good looks like</p>
                <p className="text-sm text-emerald-900 leading-relaxed whitespace-pre-line">{currentMuscle.goodLooksLike}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <p className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2">Weak looks like</p>
                <p className="text-sm text-red-900 leading-relaxed whitespace-pre-line">{currentMuscle.weakLooksLike}</p>
              </div>
            </div>
          )}
        </div>

        {/* Card body */}
        <div className="px-6 py-6 space-y-6">

          {/* Reflection question */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-4">
            <p className="text-base text-gray-800 leading-relaxed font-medium">{currentMuscle.question}</p>
          </div>

          {/* Score */}
          <div>
            <Label>How would you rate yourself?</Label>
            <DotScore value={currentScore.score} onChange={setScore} rubric={currentMuscle.rubric} />
          </div>

          {/* Impact tags */}
          <div>
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">
              What does this show?{' '}
              <span className="normal-case font-normal text-gray-500">(optional)</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {IMPACT_TAGS.map(tag => {
                const active = (currentScore.impactTags ?? []).includes(tag);
                return (
                  <button
                    key={tag}
                    onClick={() => toggleImpactTag(tag)}
                    className={`text-sm px-4 py-2 rounded-full border-2 font-medium transition-all
                      ${active
                        ? 'bg-[#011132] border-[#011132] text-white shadow-sm'
                        : 'border-gray-300 text-gray-700 bg-white hover:border-gray-500 hover:text-gray-900'}`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Evidence note */}
          <div>
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
              Evidence / Example{' '}
              <span className="normal-case font-normal text-gray-500">(optional)</span>
            </p>
            <textarea
              rows={3}
              placeholder={`e.g. "I flagged the vendor risk two weeks before it became an issue"`}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:border-[#011132]/40 focus:ring-2 focus:ring-[#011132]/10 transition-colors"
              value={currentScore.example ?? ''}
              onChange={e => setExample(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-5">
        <button
          onClick={goBack}
          disabled={currentIdx === 0}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 disabled:opacity-30 hover:text-gray-900 transition-colors border border-gray-200 px-4 py-2.5 rounded-xl hover:bg-gray-50"
        >
          <span className="material-symbols-rounded" style={{ fontSize: 18 }}>arrow_back</span>
          Back
        </button>

        <button
          onClick={goNext}
          disabled={currentScore.score === 0}
          className={`flex items-center gap-1.5 text-sm font-semibold px-6 py-2.5 rounded-xl disabled:opacity-40 transition-colors
            ${currentIdx < muscles.length - 1
              ? 'bg-[#011132] text-white hover:bg-[#0a2254]'
              : 'bg-amber-600 text-white hover:bg-amber-700'
            }`}
        >
          {currentIdx < muscles.length - 1 ? (
            <>Next Muscle <span className="material-symbols-rounded" style={{ fontSize: 18 }}>arrow_forward</span></>
          ) : (
            <>OKR Reflection <span className="material-symbols-rounded" style={{ fontSize: 18 }}>flag</span></>
          )}
        </button>
      </div>

      {currentScore.score === 0 && currentIdx < muscles.length && (
        <p className="text-center text-sm text-gray-500 mt-2">Select a score to continue</p>
      )}

      {/* All muscles overview */}
      <div className="mt-8 border-t border-gray-200 pt-5">
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">All Muscles</p>
        <div className="space-y-1">
          {muscles.map((m, i) => {
            const s     = scores.find(sc => sc.muscleKey === m.key);
            const score = s?.score ?? 0;
            const col   = score > 0 ? SCORE_COLORS[score] : null;
            return (
              <button
                key={m.key}
                onClick={() => setCurrentIdx(i)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors
                  ${i === currentIdx ? 'bg-[#011132]/8 ring-1 ring-[#011132]/20' : 'hover:bg-gray-50'}`}
              >
                <span className={`text-xs font-bold w-5 flex-shrink-0 ${score > 0 ? 'text-[#011132]' : 'text-gray-400'}`}>{i + 1}</span>
                <span className="flex-1 text-sm font-medium text-gray-800 truncate">{m.name}</span>
                {score > 0 && col ? (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs font-semibold ${col.text}`}>{SCORE_LABELS[score]}</span>
                    <span className={`w-6 h-6 rounded-full ${col.bg} flex items-center justify-center text-xs font-bold text-white`}>{score}</span>
                  </div>
                ) : (
                  <span className="text-xs font-medium text-gray-400 flex-shrink-0">—</span>
                )}
              </button>
            );
          })}

          {/* OKR Reflection row */}
          <button
            onClick={() => setWizardStep('okr_reflection')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors hover:bg-amber-50"
          >
            <span className="material-symbols-rounded text-amber-500 w-5 flex-shrink-0" style={{ fontSize: 16 }}>flag</span>
            <span className="flex-1 text-sm font-medium text-gray-800">OKR Reflection</span>
            <span className="text-xs font-medium text-amber-600 flex-shrink-0">
              {krs.length > 0 ? `${krs.length} KRs` : 'Final step'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaderReviewSelf;
